import React from 'react';
import { createRoot } from 'react-dom/client';

type ToastType = 'success' | 'error' | 'info';

interface ToastProps {
  message: string;
  type: ToastType;
  duration?: number;
}

const ToastComponent: React.FC<ToastProps> = ({ message, type, duration = 3000 }) => {
  const [isVisible, setIsVisible] = React.useState(true);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
    }, duration);

    return () => clearTimeout(timer);
  }, [duration]);

  if (!isVisible) return null;

  const bgColor = {
    success: 'bg-green-500',
    error: 'bg-red-500',
    info: 'bg-blue-500',
  }[type];

  const icon = {
    success: '✓',
    error: '✕',
    info: 'ℹ',
  }[type];

  return (
    <div
      className={`fixed top-4 right-4 z-[100] ${bgColor} text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-3 animate-slide-in-right max-w-md`}
    >
      <span className="text-xl font-bold">{icon}</span>
      <span className="text-sm font-medium">{message}</span>
    </div>
  );
};

class ToastManager {
  private container: HTMLDivElement | null = null;
  private root: any = null;

  private getContainer() {
    if (!this.container) {
      this.container = document.createElement('div');
      this.container.id = 'toast-container';
      document.body.appendChild(this.container);
    }
    return this.container;
  }

  private show(message: string, type: ToastType, duration?: number) {
    const container = this.getContainer();
    const toastElement = document.createElement('div');
    container.appendChild(toastElement);

    const root = createRoot(toastElement);
    root.render(<ToastComponent message={message} type={type} duration={duration} />);

    setTimeout(() => {
      root.unmount();
      container.removeChild(toastElement);
    }, duration || 3000);
  }

  success(message: string, duration?: number) {
    this.show(message, 'success', duration);
  }

  error(message: string, duration?: number) {
    this.show(message, 'error', duration);
  }

  info(message: string, duration?: number) {
    this.show(message, 'info', duration);
  }
}

export const toast = new ToastManager();
